/**
 * NegoKpiCards — 8 cartes KPI du Centre de Négociations (sparkline + donut).
 */
import React from 'react';
import {
  MessageSquare, Clock, AlertTriangle, CheckCircle2, Timer, DollarSign,
  TrendingUp, PieChart,
} from 'lucide-react';
import { fmtXof, fmtMinutes, GREEN } from './negoMeta';

const Sparkline = ({ data = [], color = GREEN }) => {
  const counts = (data || []).map((d) => d.count ?? d);
  if (!counts.length) return null;
  const max = Math.max(...counts, 1);
  const w = 88; const h = 26;
  const stepX = w / Math.max(counts.length - 1, 1);
  const pts = counts.map((v, i) => `${i * stepX},${h - (v / max) * (h - 3) - 2}`).join(' ');
  return (
    <svg width={w} height={h} className="overflow-visible">
      <polyline points={`0,${h} ${pts} ${w},${h}`} fill={color} opacity="0.07" />
      <polyline points={pts} fill="none" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" opacity="0.85" />
    </svg>
  );
};

const Donut = ({ pct, color = GREEN }) => {
  const r = 16; const c = 2 * Math.PI * r;
  const off = c - (Math.min(100, Math.max(0, pct || 0)) / 100) * c;
  return (
    <svg width="44" height="44" viewBox="0 0 44 44" className="shrink-0">
      <circle cx="22" cy="22" r={r} fill="none" stroke="#e2e8f0" strokeWidth="5" />
      <circle cx="22" cy="22" r={r} fill="none" stroke={color} strokeWidth="5" strokeLinecap="round"
        strokeDasharray={c} strokeDashoffset={off} transform="rotate(-90 22 22)" />
    </svg>
  );
};

const Card = ({ testId, label, icon: Icon, iconBg, iconColor, value, sub, sparkline, donut, empty }) => (
  <div
    data-testid={testId}
    className="relative overflow-hidden rounded-xl border border-slate-200 bg-white p-4 transition-all duration-200 hover:-translate-y-0.5 hover:shadow-md min-h-[140px] flex flex-col"
  >
    <div className={`mb-3 flex h-9 w-9 items-center justify-center rounded-lg ${iconBg}`}>
      <Icon className={`h-4.5 w-4.5 ${iconColor}`} style={{ width: 18, height: 18 }} />
    </div>
    <div className="text-[10px] font-bold uppercase tracking-[0.1em] text-slate-500 leading-tight">{label}</div>
    <div className="mt-1 flex items-center gap-2">
      <span className={`text-2xl font-extrabold tracking-tight tabular-nums ${empty ? 'text-slate-300' : 'text-slate-900'}`}>{value}</span>
      {donut != null && <Donut pct={donut} />}
    </div>
    {sub && <div className="mt-0.5 text-[11px] leading-snug text-slate-500">{sub}</div>}
    {sparkline && (
      <div className="pointer-events-none absolute bottom-2 right-2 opacity-90">
        <Sparkline data={sparkline} />
      </div>
    )}
  </div>
);

export const NegoKpiCards = ({ stats }) => {
  const s = stats || {};
  const spark = s.sparkline7d;
  return (
    <div className="grid grid-cols-2 gap-3 sm:grid-cols-4 xl:grid-cols-8" data-testid="nego-kpi-grid">
      <Card testId="kpi-actives" label="Négociations actives" icon={MessageSquare}
        iconBg="bg-emerald-50" iconColor="text-emerald-600" value={s.active ?? 0}
        sub={`${(s.pendingClient || 0) + (s.pendingInternal || 0)} en attente de décision`} sparkline={spark} />
      <Card testId="kpi-sla-risk" label="SLA à risque" icon={Clock}
        iconBg="bg-orange-50" iconColor="text-orange-600" value={s.overdue ?? 0}
        sub="À traiter rapidement" sparkline={spark} />
      <Card testId="kpi-escalated" label="Escaladées" icon={AlertTriangle}
        iconBg="bg-purple-50" iconColor="text-purple-600" value={s.escalated ?? 0}
        sub="En attente de validation" sparkline={spark} />
      <Card testId="kpi-winrate" label="Taux d'acceptation" icon={CheckCircle2}
        iconBg="bg-emerald-50" iconColor="text-emerald-600"
        value={s.winRate != null ? `${s.winRate}%` : '—'} empty={s.winRate == null}
        sub={`${s.accepted || 0} acceptées · ${s.rejected || 0} refusées`} donut={s.winRate || 0} />
      <Card testId="kpi-avg-time" label="Temps moyen de décision" icon={Timer}
        iconBg="bg-blue-50" iconColor="text-blue-600"
        value={fmtMinutes(s.avgResolutionMinutes)} empty={s.avgResolutionMinutes == null}
        sub="De création à résolution" sparkline={spark} />
      <Card testId="kpi-volume" label="Volume négocié" icon={DollarSign}
        iconBg="bg-emerald-50" iconColor="text-emerald-600"
        value={s.volumeXof ? fmtXof(s.volumeXof) : '0 XOF'} empty={!s.volumeXof}
        sub="Sur dossiers actifs" sparkline={spark} />
      <Card testId="kpi-gain" label="Gain négocié" icon={TrendingUp}
        iconBg="bg-amber-50" iconColor="text-amber-600"
        value={s.gainNegocie ? fmtXof(s.gainNegocie) : '0 XOF'} empty={!s.gainNegocie}
        sub="Écart positif sur acceptées" sparkline={spark} />
      <Card testId="kpi-margin" label="Marge estimée" icon={PieChart}
        iconBg="bg-violet-50" iconColor="text-violet-600"
        value={s.marginEstimate != null ? `${s.marginEstimate}%` : '—'} empty={s.marginEstimate == null}
        sub={s.marginEstimate != null ? 'Après négociation' : 'Donnée de marge indisponible'} />
    </div>
  );
};

export default NegoKpiCards;
